import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Window, Loading } from "../../components/VB";
import { toast } from "sonner";

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [changingEmail, setChangingEmail] = useState(false);
  const [profile, setProfile] = useState(null);

  // Form states
  const [emailForm, setEmailForm] = useState({ new_email: "" });
  const [passwordForm, setPasswordForm] = useState({ new_password: "", confirm_password: "" });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/me");
      setProfile(data);
      setEmailForm({ new_email: data.email || "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setLoading(false);
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!emailForm.new_email || !emailForm.new_email.includes("@")) {
      toast.error("Email tidak valid");
      return;
    }
    setChangingEmail(true);
    try {
      await api.post(`/users/${profile.id}/change-email`, { new_email: emailForm.new_email });
      toast.success("Email berhasil diubah");
      setChangingEmail(false);
      loadProfile();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setChangingEmail(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password.length < 6) {
      toast.error("Password minimal 6 karakter");
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Password dan konfirmasi password tidak sama");
      return;
    }
    setChangingPassword(true);
    try {
      await api.post(`/users/${profile.id}/change-password`, { new_password: passwordForm.new_password });
      toast.success("Password berhasil diubah");
      setPasswordForm({ new_password: "", confirm_password: "" });
      setChangingPassword(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setChangingPassword(false);
    }
  };

  if (loading) return <Loading message="Memuat profil..." />;
  if (!profile) return null;

  const roleLabels = { admin: "Administrator", client: "Client", vendor: "Vendor", karyawan: "Karyawan" };
  const roleColors = { admin: "#003399", client: "#4f8a10", vendor: "#d4a017", karyawan: "#00529b" };

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", padding: 0 }}>
      {/* Profile Info Card */}
      <Window title="Profil Saya">
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 16 }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: `linear-gradient(135deg, ${roleColors[profile.role] || "#003399"}, ${roleColors[profile.role] || "#003399"}88`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "white", fontSize: 28, fontWeight: "bold", flexShrink: 0
          }}>
            {(profile.name || profile.email || "U").charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#003399" }}>{profile.name || "-"}</div>
            <div style={{ fontSize: 13, color: "#666" }}>{profile.email}</div>
            <div style={{ marginTop: 4 }}>
              <span style={{
                display: "inline-block", padding: "2px 10px", borderRadius: 12,
                background: roleColors[profile.role] || "#003399", color: "white",
                fontSize: 11, fontWeight: 600
              }}>
                {roleLabels[profile.role] || profile.role}
              </span>
            </div>
          </div>
        </div>

        {/* Client specific info */}
        {profile.client_data && (
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            <div><strong>Client:</strong> {profile.client_data.nama || "-"}</div>
            <div><strong>Group:</strong> {profile.client_data.group_nama || "-"}</div>
          </div>
        )}

        {/* Group Client specific info */}
        {profile.group_data && (
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            <div><strong>Group Client:</strong> {profile.group_data.nama || "-"}</div>
            <div><strong>Jumlah Client:</strong> {profile.group_data.member_count ?? "-"}</div>
            {profile.group_data.deskripsi && <div><strong>Deskripsi:</strong> {profile.group_data.deskripsi}</div>}
            <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>Akun ini melihat laporan seluruh project dalam group.</div>
          </div>
        )}

        {/* Vendor specific info */}
        {profile.vendor_data && (
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
            <div><strong>Pemilik:</strong> {profile.vendor_data.pemilik || "-"}</div>
            <div><strong>Kontak:</strong> {profile.vendor_data.kontak || "-"}</div>
            <div><strong>Alamat:</strong> {profile.vendor_data.alamat || "-"}</div>
          </div>
        )}
      </Window>

      {/* Change Email */}
      <Window title="📧 Ubah Email">
        <form onSubmit={handleChangeEmail}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13, color: "#003399" }}>
              Email Baru
            </label>
            <input
              type="email"
              className="vb-input"
              value={emailForm.new_email}
              onChange={(e) => setEmailForm({ new_email: e.target.value })}
              placeholder="contoh@email.com"
              required
            />
          </div>
          <button
            type="submit"
            className="vb-btn-primary"
            disabled={changingEmail}
            style={{ minWidth: 120 }}
          >
            {changingEmail ? "Menyimpan..." : "Simpan Email"}
          </button>
        </form>
      </Window>

      {/* Change Password */}
      <Window title="🔒 Ubah Password">
        <form onSubmit={handleChangePassword}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13, color: "#003399" }}>
              Password Baru
            </label>
            <input
              type="password"
              className="vb-input"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              placeholder="Minimal 6 karakter"
              required
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 4, fontSize: 13, color: "#003399" }}>
              Konfirmasi Password Baru
            </label>
            <input
              type="password"
              className="vb-input"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              placeholder="Ketik ulang password baru"
              required
            />
            {passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
              <div style={{ color: "#d8000c", fontSize: 12, marginTop: 4 }}>⚠ Password tidak sama</div>
            )}
          </div>
          <button
            type="submit"
            className="vb-btn-primary"
            disabled={changingPassword || passwordForm.new_password !== passwordForm.confirm_password || passwordForm.new_password.length < 6}
            style={{ minWidth: 120 }}
          >
            {changingPassword ? "Menyimpan..." : "Simpan Password"}
          </button>
        </form>
      </Window>
    </div>
  );
}
